import React, { useEffect, useState, useContext } from "react";
import { Button, Row, Col } from "antd";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Paper,
  Box,
  Typography,
  Divider,
  Button as MUIButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#4B0082";

export default function HomeBannerForm() {
  const { setLoading } = useContext(LoaderContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
const [bannerRemoved, setBannerRemoved] = useState(false);

  const mode = id ? "edit" : "create";

  
  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/homebanner/${id}`);

      
      const data = res.data.data;

      if (data?.homeBannerImg) {
        setFilePreview(`${BASE_URL}${data.homeBannerImg}`);
      }
    } catch (err) {
      toast.error("Failed to load banner");
    }
  };

  useEffect(() => {
    setLoading(true);

    if (id) fetchDetails();

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [id]);

  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const removeFile = () => {
  setBannerFile(null);
  setFilePreview("");
  setBannerRemoved(true); 
};


  
  const handleSave = async () => {
  if (!bannerFile && mode === "create") {
    toast.error("Banner image is required");
    return;
  }

  try {
    setSaving(true);

    const formData = new FormData();

   
    if (bannerRemoved) {
      formData.append("removeBanner", "1");
    }

   
    if (bannerFile) {
      formData.append("homeBannerImg", bannerFile);
    }

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    if (mode === "edit") {
      await axios.patch(
        `${BASE_URL}/homebanner/${id}`,
        formData,
        config
      );
      toast.success("Banner Updated Successfully");
    } else {
      await axios.post(`${BASE_URL}/homebanner`, formData, config);
      toast.success("Banner Added Successfully");
    }

    navigate("/dashboard/homebannerlist");
  } catch (err) {
    console.log("Save Error:", err);
    toast.error("Save Failed");
  } finally {
    setSaving(false);
  }
};

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1, md: 3 }, borderRadius: 3 }}>
        
        <Box display="flex" justifyContent="center" mt={1.5} mb={1.5}>
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #b255f5)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "14px", md: "18px" },
                fontWeight: 900,
                color: "#fff",
                textTransform: "uppercase",
              }}
            >
              {mode === "edit" ? "Update Banner" : "Add Banner"}
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: { xs: 2, md: 3 },
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

        
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24}>
            <Typography fontWeight={600} mb={1} sx={{ color: themeColor }}>
              Upload Banner Image
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <input
                id="banner-upload"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />

              <label htmlFor="banner-upload">
                <MUIButton
                  variant="outlined"
                  component="span"
                sx={{ fontWeight: 600, fontSize: { xs: "12px", md: "14px" }, borderColor: "#aaa", color: "#8b8989", "&:hover": { borderColor: themeColor, background: "rgba(255,92,110,0.08)" } }}
                >
                  Upload Banner
                </MUIButton>
              </label>

              
              {filePreview && (
                <Box position="relative">
                  <img
                    src={filePreview}
                    alt="preview"
                    style={{
                      width: 80,
                      height: 50,
                      borderRadius: 6,
                      objectFit: "cover",
                      border: `1px solid ${themeColor}`,
                    }}
                  />
                  <CloseIcon
                    onClick={removeFile}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      cursor: "pointer",
                      background: "#fff",
                      borderRadius: "50%",
                      fontSize: 18,
                      color: themeColor,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Col>
        </Row>

        
        <Box display="flex" justifyContent="end" mt={4} gap={1.5}>
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={handleSave}
            loading={saving}
            style={{
              background: themeColor,
              borderRadius: 25,
              padding: "4px 20px",
            }}
          >
            {mode === "edit" ? "Update" : "Save"}
          </Button>

          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => navigate("/dashboard/homebannerlist")}
            style={{
              borderRadius: 25,
              padding: "4px 20px",
              color: "red",
              borderColor: "red",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffe6e9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Details
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
